import React from 'react'
import { Button, Card, Form, Input, Tabs, message, Typography } from 'antd'
import { Link, useNavigate, useParams } from 'react-router-dom'

import { useAuth } from '@/context/AuthContext'
import BeianFooter from '@/components/BeianFooter'

const LAST_MODULE_KEY = 'cbms_last_module'
const AUTH_EXPIRED_FLAG = 'cbms_auth_expired_redirecting'

function resolveAuthErrorMessage(error: any, fallback: string): string {
  const backendDetail = error?.response?.data?.detail
  if (backendDetail) {
    return backendDetail
  }

  const status = error?.response?.status
  const rawMessage = String(error?.message || '')
  if (status === 500 && rawMessage.includes('Request failed with status code 500')) {
    return '后端服务当前不可用（本地常见原因：127.0.0.1:8000 未启动），请先启动后端再重试'
  }

  return rawMessage || fallback
}

function LoginPage() {
  const navigate = useNavigate()
  const { portal } = useParams<{ portal: string }>()
  const isAdminPortal = portal === 'admin'
  const isUserPortal = portal === 'user'
  const { login, register, user, token, logout } = useAuth()
  const [loading, setLoading] = React.useState(false)
  const hasAutoSwitchedRef = React.useRef(false)

  React.useEffect(() => {
    if (!isUserPortal) {
      hasAutoSwitchedRef.current = false
      return
    }
    if (!token || !user?.is_admin || hasAutoSwitchedRef.current) return

    hasAutoSwitchedRef.current = true
    message.warning('检测到当前为管理员登录态，已自动退出，请使用普通用户账号登录')
    logout()
  }, [isUserPortal, logout, token, user?.is_admin])

  React.useEffect(() => {
    if (!isAdminPortal) {
      return
    }

    if (sessionStorage.getItem(AUTH_EXPIRED_FLAG) === '1') {
      sessionStorage.removeItem(AUTH_EXPIRED_FLAG)
      message.warning('登录状态已失效，请重新登录')
    }
  }, [isAdminPortal])

  const onFinish = async (values: { username: string; password: string }) => {
    if (!isAdminPortal && !isUserPortal) {
      message.error('无效入口，请返回入口选择页')
      return
    }
    setLoading(true)
    try {
      await login(values.username, values.password, isAdminPortal ? 'admin' : 'user')
      message.success('登录成功')
      const lastModulePath = localStorage.getItem(LAST_MODULE_KEY)
      navigate(lastModulePath || '/modules', { replace: true })
    } catch (error: any) {
      message.error(resolveAuthErrorMessage(error, '登录失败'))
    } finally {
      setLoading(false)
    }
  }

  const onRegister = async (values: {
    username: string
    password: string
    full_name: string
    department?: string
    manager_name?: string
  }) => {
    setLoading(true)
    try {
      await register(values)
      message.success('注册成功，请使用新账号登录')
    } catch (error: any) {
      message.error(resolveAuthErrorMessage(error, '注册失败'))
    } finally {
      setLoading(false)
    }
  }

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', background: 'linear-gradient(135deg, #f0f5ff 0%, #fafafa 100%)', padding: 24, gap: 12 }}>
      <Card style={{ width: 420, borderRadius: 12 }}>
        <Typography.Title level={3} style={{ marginTop: 0, marginBottom: 4 }}>{isAdminPortal ? '管理员入口' : '普通用户入口'}</Typography.Title>
        <Typography.Text type="secondary" style={{ display: 'block' }}>公司智慧管理中台</Typography.Text>
        <Typography.Text type="secondary">{isAdminPortal ? '仅管理员账号可登录' : '普通用户可登录或注册'}</Typography.Text>
        <div style={{ marginTop: 8 }}>
          <Link to="/entry">返回入口选择</Link>
        </div>

        {isAdminPortal ? (
          <>
            <Form layout="vertical" onFinish={onFinish} style={{ marginTop: 18 }}>
              <Form.Item label="用户名" name="username" rules={[{ required: true, message: '请输入用户名' }]}>
                <Input />
              </Form.Item>
              <Form.Item label="密码" name="password" rules={[{ required: true, message: '请输入密码' }]}>
                <Input.Password />
              </Form.Item>
              <Button type="primary" htmlType="submit" block loading={loading}>登录</Button>
            </Form>
          </>
        ) : (
          <Tabs
            style={{ marginTop: 12 }}
            items={[
              {
                key: 'login',
                label: '用户登录',
                children: (
                  <Form layout="vertical" onFinish={onFinish}>
                    <Form.Item label="用户名" name="username" rules={[{ required: true, message: '请输入用户名' }]}>
                      <Input />
                    </Form.Item>
                    <Form.Item label="密码" name="password" rules={[{ required: true, message: '请输入密码' }]}>
                      <Input.Password />
                    </Form.Item>
                    <Button type="primary" htmlType="submit" block loading={loading}>登录</Button>
                  </Form>
                ),
              },
              {
                key: 'register',
                label: '用户注册',
                children: (
                  <Form layout="vertical" onFinish={onRegister}>
                    <Form.Item label="用户名" name="username" rules={[{ required: true, message: '请输入用户名' }, { min: 3, message: '用户名至少 3 位' }]}>
                      <Input />
                    </Form.Item>
                    <Form.Item label="密码" name="password" rules={[{ required: true, message: '请输入密码' }, { min: 6, message: '密码至少 6 位' }]}>
                      <Input.Password />
                    </Form.Item>
                    <Form.Item label="姓名" name="full_name" rules={[{ required: true, message: '请输入姓名' }]}>
                      <Input />
                    </Form.Item>
                    <Form.Item label="部门" name="department">
                      <Input />
                    </Form.Item>
                    <Form.Item label="直属领导" name="manager_name">
                      <Input />
                    </Form.Item>
                    <Button type="primary" htmlType="submit" block loading={loading}>注册</Button>
                  </Form>
                ),
              },
            ]}
          />
        )}
      </Card>
      <BeianFooter style={{ paddingBottom: 0 }} />
    </div>
  )
}

export default LoginPage
